import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KIcon } from '../shared/components/KIcon.js';
import clsx from 'clsx';
import type { NavItem } from '../shared/types/index.js';

/**
 * Sub-navigatie (tabbalk) van een module. Items met `menu: 'meer'` staan onder "Meer ▾".
 * Het menu is `position: fixed`, zodat de horizontaal scrollbare tabbalk (mobiel) het niet afknipt.
 * Kordaro v4: tabs 44 hoog met brand-onderlijn + aria-current, enkel tekst (geen emoji uit de manifests).
 */
export function ModuleSubNav({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const meerKnop = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const label = (item: NavItem) => (item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label);
  const hoofd = items.filter(i => i.menu !== 'meer');
  const meer = items.filter(i => i.menu === 'meer');
  const actiefInMeer = meer.find(i => location.pathname === i.path);

  // Menu sluiten bij navigatie, klik buiten het menu, scroll of resize.
  useEffect(() => { setMenuPos(null); }, [location.pathname]);
  useEffect(() => {
    if (!menuPos) return;
    const sluit = (e: Event) => {
      const doel = e.target as Node;
      if (menuRef.current?.contains(doel) || meerKnop.current?.contains(doel)) return;
      setMenuPos(null);
    };
    const sluitAltijd = () => setMenuPos(null);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuPos(null); };
    document.addEventListener('mousedown', sluit);
    document.addEventListener('touchstart', sluit);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', sluitAltijd);
    window.addEventListener('scroll', sluitAltijd, true);
    return () => {
      document.removeEventListener('mousedown', sluit);
      document.removeEventListener('touchstart', sluit);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', sluitAltijd);
      window.removeEventListener('scroll', sluitAltijd, true);
    };
  }, [menuPos]);

  const toggleMenu = () => {
    if (menuPos) { setMenuPos(null); return; }
    const r = meerKnop.current?.getBoundingClientRect();
    if (!r) return;
    const breedte = 220;
    setMenuPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - breedte - 8)) });
  };

  const knop = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    return (
      <button
        key={item.path}
        type="button"
        onClick={() => navigate(item.path)}
        aria-current={isActive ? 'page' : undefined}
        className="k-tab"
      >
        {label(item)}
        {item.badge !== undefined && item.badge > 0 && (
          <span className="k-nav-badge !ml-1">{item.badge}</span>
        )}
      </button>
    );
  };

  return (
    <nav aria-label="Onderdelen van deze module" className="k-tabs k-verberg-volscherm bg-[var(--k-bg)] px-4 md:px-6 lg:px-8 flex-shrink-0">
      {hoofd.map(knop)}
      {meer.length > 0 && (
        <>
          <button
            ref={meerKnop}
            type="button"
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={!!menuPos}
            aria-current={actiefInMeer ? 'page' : undefined}
            className="k-tab"
          >
            {actiefInMeer ? label(actiefInMeer) : t('common.more', { defaultValue: 'Meer' })}
            <KIcon naam="chevron" size={16} className="rotate-90" />
          </button>
          {menuPos && (
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-50 w-[220px] bg-white border border-card-border rounded-lg shadow-lg py-1"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              {meer.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    type="button"
                    role="menuitem"
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => navigate(item.path)}
                    className={clsx(
                      'w-full text-left px-4 min-h-[44px] text-sm flex items-center gap-2 transition-colors',
                      isActive ? 'bg-brand-light text-brand font-semibold' : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    {label(item)}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </nav>
  );
}
